import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { justificatifService } from '../../services/api';
import { Upload, FileText, CheckCircle, X, ArrowLeft } from 'lucide-react';
import SofincoLogo from '../../components/SofincoLogo';

const BulletinSalaireUpload = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Create preview for images, show file name for PDF
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFile(selectedFile);
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setFile(selectedFile);
      setPreview('pdf');
    }
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      // Upload document
      if (file) {
        await justificatifService.upload(user.id, 'bulletin_salaire', file);
      }

      // Créer le justificatif validé pour marquer l'étape comme complétée
      await justificatifService.createValidated(user.id, 'bulletin_salaire');

      // Redirect to success page
      navigate('/client/justificatif-success', { 
        state: { 
          type: 'bulletin_salaire',
          title: 'Bulletin de salaire'
        } 
      });
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Erreur lors de l\'upload. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <SofincoLogo className="h-10" />
          <button 
            onClick={() => navigate('/client/dashboard')}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back button */}
        <button 
          onClick={() => navigate('/client/dashboard')}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </button>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Bulletin de salaire</h1>
        <p className="text-gray-600 mb-8">
          Veuillez télécharger vos 3 derniers bulletins de salaire.
        </p>

        {/* Upload Section */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bulletin de salaire *
            </label>
            {preview ? (
              <div className="relative">
                {preview === 'pdf' ? (
                  <div className="w-full h-48 border-2 border-gray-200 rounded-lg flex flex-col items-center justify-center bg-gray-50">
                    <FileText className="h-16 w-16 text-purple-600 mb-2" />
                    <p className="text-sm text-gray-700 font-medium">{file.name}</p>
                    <p className="text-xs text-gray-500 mt-1">Document PDF</p>
                  </div>
                ) : (
                  <img 
                    src={preview} 
                    alt="Bulletin" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                )}
                <button
                  onClick={removeFile}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {file.name}
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                <Upload className="h-10 w-10 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">Cliquez pour télécharger</span>
                <span className="text-xs text-gray-400 mt-1">PDF, JPG ou PNG (max 10MB)</span>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8">
          <Button
            onClick={handleSubmit}
            disabled={!file || uploading}
            className="w-full text-white font-semibold py-4 text-base"
            style={{ backgroundColor: file ? '#8B5CF6' : '#9CA3AF' }}
          >
            {uploading ? 'Envoi en cours...' : 'Valider et envoyer'}
          </Button>
        </div>

        {/* Info */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Important :</strong> Vos bulletins de salaire doivent être récents (3 derniers mois) 
            et lisibles. Assurez-vous que votre nom, prénom et salaire net sont visibles.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BulletinSalaireUpload;
